import React, { useState, useEffect, useRef } from 'react';
import './AnimatedGemPreview.css';

/**
 * AnimatedGemPreview
 *
 * A mesmerizing animated gemstone that cycles through all the gem types
 * used in the socket display above. Creates a visual connection between
 * the "Pair your first device" prompt and the gem sockets.
 *
 * Features:
 * - Morphs between gem shapes (round, oval, emerald, pear, marquise, princess, hexagon, trillion)
 * - Transitions colors smoothly
 * - Gentle wobble/shake animations during transitions
 * - Subtle rotation and scale pulses
 * - Particle effects during transformation
 */

// Same gem definitions as Topnav.RemoteUsers.js for consistency
const GEMS = [
  { color: '#e91e63', glow: 'rgba(233, 30, 99, 0.6)', name: 'Ruby', shape: 'round' },
  { color: '#9c27b0', glow: 'rgba(156, 39, 176, 0.6)', name: 'Amethyst', shape: 'oval' },
  { color: '#3f51b5', glow: 'rgba(63, 81, 181, 0.6)', name: 'Sapphire', shape: 'emerald' },
  { color: '#00bcd4', glow: 'rgba(0, 188, 212, 0.6)', name: 'Aquamarine', shape: 'pear' },
  { color: '#4caf50', glow: 'rgba(76, 175, 80, 0.6)', name: 'Emerald', shape: 'emerald' },
  { color: '#ff9800', glow: 'rgba(255, 152, 0, 0.6)', name: 'Topaz', shape: 'marquise' },
  { color: '#f44336', glow: 'rgba(244, 67, 54, 0.6)', name: 'Garnet', shape: 'princess' },
  { color: '#e91e8f', glow: 'rgba(233, 30, 143, 0.6)', name: 'Tourmaline', shape: 'hexagon' },
  { color: '#7c4dff', glow: 'rgba(124, 77, 255, 0.6)', name: 'Tanzanite', shape: 'trillion' },
];

const AnimatedGemPreview = ({
  cycleInterval = 3000,  // Time between gem changes
  size = 24              // Base size in pixels
}) => {
  const [currentGemIndex, setCurrentGemIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [particles, setParticles] = useState([]);
  const particleIdRef = useRef(0);

  const currentGem = GEMS[currentGemIndex];
  const nextGemIndex = (currentGemIndex + 1) % GEMS.length;
  const nextGem = GEMS[nextGemIndex];

  // Cycle through gems
  useEffect(() => {
    const interval = setInterval(() => {
      // Start transition animation
      setIsTransitioning(true);

      // Spawn transformation particles
      spawnParticles();

      // After transition completes, change the gem
      setTimeout(() => {
        setCurrentGemIndex(prev => (prev + 1) % GEMS.length);
        setIsTransitioning(false);
      }, 600); // Transition duration
    }, cycleInterval);

    return () => clearInterval(interval);
  }, [cycleInterval]);

  // Spawn magical particles during transition
  const spawnParticles = () => {
    const newParticles = [];
    const particleCount = 6;

    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        id: particleIdRef.current++,
        angle: (360 / particleCount) * i + Math.random() * 30,
        distance: 20 + Math.random() * 15,
        duration: 600 + Math.random() * 200,
        size: 2 + Math.random() * 3,
        delay: Math.random() * 100
      });
    }

    setParticles(prev => [...prev, ...newParticles]);

    // Clean up particles after animation
    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.includes(p)));
    }, 1000);
  };

  return (
    <div
      className={`animated-gem-preview ${isTransitioning ? 'transitioning' : ''}`}
      style={{
        '--gem-size': `${size}px`,
        '--gem-color': currentGem.color,
        '--gem-glow': currentGem.glow,
        '--next-gem-color': nextGem.color,
        '--next-gem-glow': nextGem.glow
      }}
    >
      {/* Ambient glow layer */}
      <div className="gem-ambient-glow" />

      {/* Main gem body with shape morphing */}
      <div className={`gem-body gem-shape-${currentGem.shape}`}>
        {/* Facet highlights */}
        <div className="gem-facet facet-1" />
        <div className="gem-facet facet-2" />
        <div className="gem-facet facet-3" />

        {/* Core shine */}
        <div className="gem-shine" />

        {/* Inner sparkle */}
        <div className="gem-sparkle" />
      </div>

      {/* Transformation particles */}
      <div className="particle-container">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="transform-particle"
            style={{
              '--particle-angle': `${particle.angle}deg`,
              '--particle-distance': `${particle.distance}px`,
              '--particle-duration': `${particle.duration}ms`,
              '--particle-size': `${particle.size}px`,
              '--particle-delay': `${particle.delay}ms`
            }}
          />
        ))}
      </div>

      {/* Subtle rotating ring */}
      <div className="gem-orbit" />
    </div>
  );
};

export default AnimatedGemPreview;
